import React, { useState } from 'react';
import { playbooksAPI } from '../services/api';

const SERVER_TYPE_OPTIONS = [
  { value: '',          label: 'Any (K8s + Baremetal)' },
  { value: 'k8s',       label: 'Kubernetes only' },
  { value: 'baremetal', label: 'Baremetal only' },
];

const SEVERITY_OPTIONS = [
  { value: '',         label: 'Any severity' },
  { value: 'critical', label: 'Critical' },
  { value: 'warning',  label: 'Warning' },
  { value: 'info',     label: 'Info' },
];

/**
 * PlaybookFormModal — reusable create/edit playbook modal.
 *
 * Props:
 *   initialForm  — pre-filled form values (e.g. from an alert)
 *   editId       — null = create, number = update existing
 *   onSaved      — callback after successful save (receives saved playbook)
 *   onClose      — callback to close the modal
 */
export default function PlaybookFormModal({ initialForm = {}, editId = null, onSaved, onClose }) {
  const [form, setForm] = useState({
    name:                 '',
    alert_name_pattern:   '',
    server_type:          '',
    severity:             '',
    namespace_pattern:    '',
    instructions:         '',
    log_paths_raw:        '',
    diagnostic_hints_raw: '',
    resolution_steps_raw: '',   // one step per line: "Title | command | destructive"
    is_active:            true,
    ...initialForm,
  });
  const [saving, setSaving]     = useState(false);
  const [formError, setFormError] = useState(null);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setFormError('Name is required'); return; }
    setSaving(true);
    setFormError(null);
    try {
      const logPaths = form.log_paths_raw
        .split(/[\n,]+/)
        .map(s => s.trim())
        .filter(Boolean);
      const diagHints = form.diagnostic_hints_raw
        .split(/\n+/)
        .map(s => s.trim())
        .filter(Boolean);
      // Each line: "Title | command | destructive" (command + destructive optional)
      const resSteps = (form.resolution_steps_raw || '')
        .split(/\n+/)
        .map(s => s.trim())
        .filter(Boolean)
        .map(line => {
          const [title, command, flag] = line.split('|').map(x => (x || '').trim());
          const destructive = /destruct/i.test(flag || '');
          return {
            title: title || 'step',
            action_type: command ? 'kubectl_exec' : 'manual',
            command: command || '',
            is_destructive: destructive,
            requires_approval: destructive,
            rollback: null,
            notes: '',
          };
        });

      const payload = {
        name:               form.name.trim(),
        alert_name_pattern: form.alert_name_pattern.trim() || null,
        server_type:        form.server_type || null,
        severity:           form.severity || null,
        namespace_pattern:  form.namespace_pattern.trim() || null,
        instructions:       form.instructions.trim() || null,
        log_paths:          logPaths.length ? logPaths : null,
        diagnostic_hints:   diagHints.length ? diagHints : null,
        resolution_steps:   resSteps.length ? resSteps : null,
        is_active:          form.is_active,
      };

      let res;
      if (editId) {
        res = await playbooksAPI.update(editId, payload);
      } else {
        res = await playbooksAPI.create(payload);
      }
      if (onSaved) onSaved(res.data);
      onClose();
    } catch (err) {
      setFormError(err.response?.data?.detail || 'Failed to save playbook');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="pb-modal-overlay" onClick={onClose}>
      <div className="pb-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 640 }}>
        <div className="pb-modal-header">
          <h2>{editId ? 'Edit Playbook' : 'Create Playbook'}</h2>
          <button className="pb-modal-close" onClick={onClose}>✕</button>
        </div>

        <form onSubmit={handleSave} className="pb-form">
          {formError && <div className="pb-form-error">{formError}</div>}

          <label>Playbook Name *</label>
          <input
            className="pb-input"
            placeholder='e.g. "Host High CPU Load", "Disk Space Alert"'
            value={form.name}
            onChange={e => setForm({ ...form, name: e.target.value })}
          />

          <div className="pb-form-section-title">Match Criteria (all optional — leave blank = matches everything)</div>

          <label>Alert Name Contains</label>
          <input
            className="pb-input"
            placeholder='e.g. "CPU Load" or "DiskPressure" — leave blank for all alerts'
            value={form.alert_name_pattern}
            onChange={e => setForm({ ...form, alert_name_pattern: e.target.value })}
          />

          <div className="pb-form-row">
            <div>
              <label>Server Type</label>
              <select
                className="pb-select"
                value={form.server_type}
                onChange={e => setForm({ ...form, server_type: e.target.value })}
              >
                {SERVER_TYPE_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label>Severity</label>
              <select
                className="pb-select"
                value={form.severity}
                onChange={e => setForm({ ...form, severity: e.target.value })}
              >
                {SEVERITY_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>

          <label>Namespace Contains</label>
          <input
            className="pb-input"
            placeholder='e.g. "production" — leave blank for all namespaces'
            value={form.namespace_pattern}
            onChange={e => setForm({ ...form, namespace_pattern: e.target.value })}
          />

          <div className="pb-form-section-title">Playbook Content</div>

          <label>Instructions for AI Analysis</label>
          <textarea
            className="pb-textarea"
            rows={5}
            placeholder={`Tell the AI what to focus on when analysing this alert. Examples:
• Identify the top CPU-consuming pods and calculate % of node capacity
• For memory pressure: check for OOMKilled containers and missing resource limits
• This cluster runs Elasticsearch — high CPU from java processes is expected up to 40%
• Always check if HPA is configured before recommending manual scaling`}
            value={form.instructions}
            onChange={e => setForm({ ...form, instructions: e.target.value })}
          />

          <label>Log Paths to Inspect (one per line)</label>
          <textarea
            className="pb-textarea pb-textarea-sm"
            rows={3}
            placeholder={`/var/log/nginx/\n/var/log/app/`}
            value={form.log_paths_raw}
            onChange={e => setForm({ ...form, log_paths_raw: e.target.value })}
          />

          <label>Diagnostic Commands (one per line)</label>
          <textarea
            className="pb-textarea pb-textarea-sm"
            rows={5}
            placeholder={`ps aux --sort=-%cpu | head -20\ntop -bn1 | head -30\nuptime\ncat /proc/loadavg`}
            value={form.diagnostic_hints_raw}
            onChange={e => setForm({ ...form, diagnostic_hints_raw: e.target.value })}
          />
          <p className="pb-hint">
            When this playbook matches, the AI decides which of these commands to run on the
            affected node and includes output in its analysis.
          </p>

          <label>Resolution Steps (one per line: <code>Title | command | destructive</code>)</label>
          <textarea
            className="pb-textarea pb-textarea-sm"
            rows={5}
            placeholder={`Truncate old script logs | kubectl exec -n {namespace} {pod} -- sh -c 'truncate -s 0 /api/SarvLogs/scriptLogs/*.log' | destructive\nEnable log rotation | | `}
            value={form.resolution_steps_raw}
            onChange={e => setForm({ ...form, resolution_steps_raw: e.target.value })}
          />
          <p className="pb-hint">
            The vetted FIX (remediation half of the runbook). The AI prefers these SRE-approved
            steps over inventing its own. Mark a step <code>destructive</code> to require approval
            before it can run. Command is optional (leave blank for a manual step).
          </p>

          <div className="pb-form-row pb-form-row-end">
            <label className="pb-toggle-label">
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={e => setForm({ ...form, is_active: e.target.checked })}
              />
              Active (fires automatically)
            </label>
            <div className="pb-form-buttons">
              <button type="button" className="btn-cancel" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn-primary" disabled={saving}>
                {saving ? 'Saving...' : editId ? 'Save Changes' : 'Create Playbook'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
